import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Paper,
  Box,
  CssBaseline,
  ThemeProvider,
  createTheme,
  IconButton,
  Select, // Added for dropdowns
  MenuItem, // Added for dropdowns
  InputLabel, // Added for dropdowns
  FormControl, // Added for dropdowns
} from "@mui/material";
import { Brightness4, Brightness7 } from "@mui/icons-material";
import LoginForm from "./LoginForm";
import TradeSummary, { Summary } from "./TradeSummary";
import TradeTable from "./TradeTable";

export interface Trade {
  filltime: string;
  tradingsymbol: string;
  expirydate: string;
  transactiontype: "BUY" | "SELL";
  strikeprice: number;
  optiontype: string;
  fillsize: number;
  fillprice: number;
  tradevalue: number;
  orderid: string;
  tradedate: string;
}

const useTradeSummary = (trades: Trade[], sortField: keyof Summary, sortDirection: 'asc' | 'desc') => {
  return useMemo(() => {
    const summaryMap: Record<string, Summary> = {};
    let totalPnl = 0;
    const positions: Record<string, { quantity: number; price: number }[]> = {};
    const sortedTrades = [...trades].sort((a, b) => {
      const da = +new Date(a.tradedate), db = +new Date(b.tradedate);
      if (da !== db) return da - db;
      return a.filltime.localeCompare(b.filltime);
    });

    for (const tr of sortedTrades) {
      const key = `${tr.tradedate}__${tr.tradingsymbol}`;
      if (!summaryMap[key]) summaryMap[key] = { date: tr.tradedate, symbol: tr.tradingsymbol, trades: 0, pnl: 0 };
      summaryMap[key].trades++;
      positions[tr.tradingsymbol] ||= [];
      if (tr.transactiontype === "BUY") positions[tr.tradingsymbol].push({ quantity: tr.fillsize, price: tr.fillprice });
      else {
        let rem = tr.fillsize, profit = 0;
        const q = positions[tr.tradingsymbol];
        while (rem && q.length) {
          const lot = q[0];
          const m = Math.min(lot.quantity, rem);
          profit += m * (tr.fillprice - lot.price);
          lot.quantity -= m;
          rem -= m;
          if (!lot.quantity) q.shift();
        }
        summaryMap[key].pnl += profit;
        totalPnl += profit;
      }
    }
    const summaryList = Object.values(summaryMap).sort((a, b) => {
      const aVal = a[sortField], bVal = b[sortField];
      if (aVal === bVal) return 0;
      return sortDirection === "asc" ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
    return { summaryList, totalPnl };
  }, [trades, sortField, sortDirection]);
};

export default function TradeVisualizer() {
  const [trades, setTrades] = useState<Trade[]>([]);
  const [selected, setSelected] = useState<{ date: string; symbol: string } | null>(null);
  const [token, setToken] = useState(localStorage.getItem("access_token") || "");
  const [username, setUsername] = useState(localStorage.getItem("username") || "");
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [selectedWeek, setSelectedWeek] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [themeMode, setThemeMode] = useState<"light" | "dark">("light");
  const [sortField, setSortField] = useState<keyof Summary>("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const theme = createTheme({ palette: { mode: themeMode } });
  const API_BASE = process.env.REACT_APP_API_BASE_URL || window.location.origin;

  const fetchTrades = useCallback(async () => {
      let allTrades: Trade[] = [];
    let skip = 0;
    const limit = 100; // Matching the backend's default limit
    let hasMore = true;

    while (hasMore) {
    try {
      const res = await fetch(`${API_BASE}/api/trades?skip=${skip}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
          console.error(`Failed to fetch trades: ${res.status} ${res.statusText}`);
          // Optionally, handle 401 Unauthorized here (e.g., try refreshing token or force logout)
          hasMore = false; // Stop fetching on error
          break;
        }
      const data = await res.json();
      if (Array.isArray(data)) {
        allTrades = allTrades.concat(data.map((t: any) => ({
            filltime: t.filltime,
            tradingsymbol: t.tradingsymbol,
            expirydate: t.expirydate,
            transactiontype: t.transactiontype,
            strikeprice: Number(t.strikeprice),
            optiontype: t.optiontype,
            fillsize: Number(t.fillsize),
            fillprice: Number(t.fillprice),
            tradevalue: Number(t.tradevalue),
            orderid: t.orderid,
            tradedate: new Date(t.tradedate).toISOString().split("T")[0],
          })));

          if (data.length < limit) {
            hasMore = false; // No more trades to fetch
          } else {
            skip += limit; // Increment skip for the next request
          }
        } else {
          console.error("API returned non-array data:", data);
          hasMore = false; // Stop if data is not an array
      }
    } catch (err) {
      console.error(err);
      hasMore = false; // Stop fetching on network error
    }
   }
    setTrades(allTrades);
  }, [token, API_BASE]); // Dependencies for useCallback

  // Date utility functions
  const getYearMonth = useCallback((dateString: string) => dateString.substring(0, 7), []); // YYYY-MM

  const getWeekStartDate = useCallback((dateString: string) => {
    // Ensure date is parsed correctly without timezone issues by adding 'T00:00:00' for consistent parsing
    const d = new Date(dateString + 'T00:00:00');
    const day = d.getDay(); // 0 for Sunday, 1 for Monday, etc.
    // Calculate difference to get to Monday (day 1). If current day is Sunday (0), go back 6 days.
    // Otherwise, go back (day - 1) days.
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    return monday.toISOString().split('T')[0]; // YYYY-MM-DD
  }, []);


  const getUniqueValues = useCallback(<T,>(arr: T[], extractor: (item: T) => string): string[] => {
    const values = new Set<string>();
    arr.forEach(item => values.add(extractor(item)));
    return Array.from(values).sort();
  }, []);

  // Filter trades based on selected month, week, day
  const filteredTradesByDate = useMemo(() => {
    let currentTrades = trades; // Start with all fetched trades
    if (selectedMonth) currentTrades = currentTrades.filter(t => getYearMonth(t.tradedate) === selectedMonth);
    if (selectedWeek) currentTrades = currentTrades.filter(t => getWeekStartDate(t.tradedate) === selectedWeek);
    if (selectedDay) currentTrades = currentTrades.filter(t => t.tradedate === selectedDay);
    return currentTrades;
  }, [trades, selectedMonth, selectedWeek, selectedDay, getYearMonth, getWeekStartDate]);

  useEffect(() => {
    if (token) fetchTrades();
  }, [token, fetchTrades]);

  const login = async () => {
    const res = await fetch(`${API_BASE}/api/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(credentials),
    });
    const json = await res.json();
    if (json.access_token && json.refresh_token) {
      localStorage.setItem("access_token", json.access_token);
      localStorage.setItem("refresh_token", json.refresh_token);
      localStorage.setItem("username", credentials.username);
      setUsername(credentials.username);
      setToken(json.access_token);
    } else {
      alert("Login failed");
    }
  };

  const logout = () => {
    localStorage.clear();
    setToken("");
    setUsername("");
    setTrades([]);
    setSelected(null);
  };

  const storeTodayTrades = async () => {
    try {
      let res = await fetch(`${API_BASE}/api/trades/store`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        const refresh = localStorage.getItem("refresh_token");
        if (refresh) {
          const r = await fetch(`${API_BASE}/api/refresh`, {
          method: "POST",
          headers: { Authorization: `Bearer ${refresh}` },
        });
          const jr = await r.json();
          if (jr.access_token) {
            localStorage.setItem("access_token", jr.access_token);
            setToken(jr.access_token);
            res = await fetch(`${API_BASE}/api/trades/store`, {
            method: "POST",
              headers: { Authorization: `Bearer ${jr.access_token}` },
            });
          } else {
            console.error("Failed to refresh token:", jr);
            alert("Session expired. Please log in again.");
            logout(); // Force logout if refresh fails
            return;
          }
        } else {
          console.warn("No refresh token found. User needs to re-authenticate.");
          alert("Session expired. Please log in again.");
          logout(); // Force logout if no refresh token
          return;
        }
      }
      if (!res.ok) {
        console.error("Store trades failed:", res.status, res.statusText);
        alert(`Failed to store trades: ${res.statusText}`);
      } else {
        console.log("Trades stored successfully.");
      }
    } catch (err) {
      console.error("Error during storeTodayTrades:", err);
      alert("An error occurred while storing trades.");
    }
    await fetchTrades();
  };

  // The useTradeSummary hook now receives the filtered trades
  const { summaryList, totalPnl } = useTradeSummary(filteredTradesByDate, sortField, sortDirection);

  // Detail trades
  const detailedTrades = selected
    // Filter from the already date-filtered trades
    ? filteredTradesByDate.filter(t => t.tradedate === selected.date && t.tradingsymbol === selected.symbol)
    : filteredTradesByDate;
  const sortedFiltered = [...detailedTrades].sort((a, b) => a.filltime.localeCompare(b.filltime));

  // Options for month, week, day selectors
  const availableMonths = useMemo(() => getUniqueValues(trades, t => getYearMonth(t.tradedate)), [trades]);
  const availableWeeks = useMemo(() => {
    const tradesForMonth = selectedMonth ? trades.filter(t => getYearMonth(t.tradedate) === selectedMonth) : trades;
    return getUniqueValues(tradesForMonth, t => getWeekStartDate(t.tradedate));
  }, [trades, selectedMonth]);
  const availableDays = useMemo(() => {
    let tradesForPeriod = trades;
    if (selectedMonth) {
      tradesForPeriod = tradesForPeriod.filter(t => getYearMonth(t.tradedate) === selectedMonth);
    }
    if (selectedWeek) {
      tradesForPeriod = tradesForPeriod.filter(t => getWeekStartDate(t.tradedate) === selectedWeek);
    }
    return getUniqueValues(tradesForPeriod, t => t.tradedate);
  }, [trades, selectedMonth, selectedWeek]);

  // Handle sorting for TradeSummary
  const handleSort = (field: keyof Summary) => {
    if (field === sortField) {
      setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  if (!token) {
    return <LoginForm credentials={credentials} setCredentials={setCredentials} onLogin={login} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Trade Visualizer
          </Typography>
          <Typography variant="subtitle1" sx={{ mr: 2 }}>
            User: {username}
          </Typography>
          <Typography variant="subtitle1" sx={{ mr: 2 }}>
            Total PnL: {totalPnl.toFixed(2)}
          </Typography>
          <IconButton
            color="inherit"
            onClick={() => setThemeMode(prev => (prev === "light" ? "dark" : "light"))}
          >
            {themeMode === "dark" ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
          <Button color="inherit" onClick={logout}>
            Logout
          </Button>
          <Button color="inherit" onClick={storeTodayTrades}>
            Store Trades
          </Button>
        </Toolbar>
      </AppBar>
      {/* Date Filters */}
      <Box sx={{ display: 'flex', gap: 2, my: 3, justifyContent: 'center' }}>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="month-select-label">Month</InputLabel>
          <Select
            labelId="month-select-label"
            value={selectedMonth || ''}
            label="Month"
            onChange={(e) => {
              setSelectedMonth(e.target.value as string || null);
              setSelectedWeek(null); // Reset lower levels
              setSelectedDay(null);
              setSelected(null); // Reset summary selection
            }}
          >
            <MenuItem value=""><em>All Months</em></MenuItem>
            {availableMonths.map(month => (
              <MenuItem key={month} value={month}>{month}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }} disabled={!selectedMonth}>
          <InputLabel id="week-select-label">Week</InputLabel>
          <Select
            labelId="week-select-label"
            value={selectedWeek || ''}
            label="Week"
            onChange={(e) => {
              setSelectedWeek(e.target.value as string || null);
              setSelectedDay(null); // Reset lower level
              setSelected(null); // Reset summary selection
            }}
          >
            <MenuItem value=""><em>All Weeks</em></MenuItem>
            {availableWeeks.map(week => (
              <MenuItem key={week} value={week}>{week}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }} disabled={!selectedWeek}>
          <InputLabel id="day-select-label">Day</InputLabel>
          <Select
            labelId="day-select-label"
            value={selectedDay || ''}
            label="Day"
            onChange={(e) => {
              setSelectedDay(e.target.value as string || null);
              setSelected(null); // Reset summary selection
            }}
          >
            <MenuItem value=""><em>All Days</em></MenuItem>
            {availableDays.map(day => (
              <MenuItem key={day} value={day}>{day}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 2, mb: 3 }}>
          <TradeSummary
            summaryList={summaryList}
            onSelect={setSelected}
            sortField={sortField}
            sortDirection={sortDirection} // Pass the sort direction
            onSort={handleSort} // Pass the new handleSort function
          />
        </Paper>
        <Paper sx={{ p: 2 }}>
          <TradeTable trades={sortedFiltered} onReset={() => setSelected(null)} />
        </Paper>
      </Container>
    </ThemeProvider>
  );
}
